import { API_BASE_URL } from '../apiBaseUrl';

const buildUrl = (path, params) => {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
};

const waitForCurrentUser = (firebase, timeoutMs = 10000) =>
  new Promise((resolve, reject) => {
    const auth = firebase?.auth;
    if (!auth?.onAuthStateChanged) {
      reject(new Error('Firebase auth is not available.'));
      return;
    }

    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    const timer = setTimeout(() => {
      unsubscribe?.();
      reject(new Error('No signed-in Firebase user found.'));
    }, timeoutMs);

    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        if (!user) return;
        clearTimeout(timer);
        unsubscribe();
        resolve(user);
      },
      (error) => {
        clearTimeout(timer);
        unsubscribe();
        reject(error);
      },
    );
  });

const getIdToken = async (firebase) => {
  const currentUser = firebase?.auth?.currentUser || (await waitForCurrentUser(firebase));
  return currentUser.getIdToken();
};

const parseError = async (response) => {
  try {
    const data = await response.json();
    return data;
  } catch (_err) {
    return { error: response.statusText };
  }
};

const notification404Fallback = (path) => {
  const normalizedPath = String(path || '');
  if (normalizedPath.includes('/unread-count')) {
    return { count: 0, unavailable: true };
  }
  if (normalizedPath.includes('/login-greeting')) {
    return {
      ok: true,
      skipped: true,
      notifications: [],
      decision: { sendWelcome: false, sendWelcomeBack: false, todayKey: '' },
      unavailable: true,
    };
  }
  if (normalizedPath.includes('/preferences')) {
    return { preferences: [], unavailable: true };
  }
  if (normalizedPath.includes('/read-bulk') || normalizedPath.includes('/archive-bulk')) {
    return { updated: 0, unavailable: true };
  }
  if (normalizedPath.match(/\/api\/notifications\/[^/]+\/(read|popup-seen|archive|unarchive|delete)$/)) {
    return { notification: null, deleted: false, unavailable: true };
  }
  return { items: [], nextCursor: null, unavailable: true };
};

const apiFetch = async (firebase, path, options = {}, params) => {
  const token = await getIdToken(firebase);
  const method = (options.method || 'GET').toUpperCase();
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const response = await fetch(buildUrl(path, params), {
    ...options,
    cache: method === 'GET' ? 'no-store' : options.cache,
    headers,
  });

  if (response.status === 304) {
    return { notModified: true };
  }

  if (response.status === 404) {
    return notification404Fallback(path);
  }

  if (!response.ok) {
    const payload = await parseError(response);
    const error = new Error(payload.error || 'Notification API request failed.');
    error.code = payload.code || 'NOTIFICATION_API_ERROR';
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
};

export const loginGreeting = async (firebase, payload = {}) =>
  apiFetch(firebase, '/api/notifications/login-greeting', {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  });

export const listNotifications = async (firebase, params = {}) =>
  apiFetch(firebase, '/api/notifications', { method: 'GET' }, params);

export const unreadCount = async (firebase) =>
  apiFetch(firebase, '/api/notifications/unread-count', { method: 'GET' });

export const markNotificationRead = async (firebase, notificationId) =>
  apiFetch(firebase, `/api/notifications/${notificationId}/read`, { method: 'POST' });

export const markNotificationPopupSeen = async (firebase, notificationId) =>
  apiFetch(firebase, `/api/notifications/${notificationId}/popup-seen`, { method: 'POST' });

export const markNotificationsReadBulk = async (firebase, ids) =>
  apiFetch(firebase, '/api/notifications/read-bulk', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });

export const archiveNotification = async (firebase, notificationId) =>
  apiFetch(firebase, `/api/notifications/${notificationId}/archive`, { method: 'POST' });

export const unarchiveNotification = async (firebase, notificationId) =>
  apiFetch(firebase, `/api/notifications/${notificationId}/unarchive`, { method: 'POST' });

export const deleteNotification = async (firebase, notificationId) =>
  apiFetch(firebase, `/api/notifications/${notificationId}/delete`, { method: 'POST' });

export const archiveNotificationsBulk = async (firebase, ids) =>
  apiFetch(firebase, '/api/notifications/archive-bulk', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });

export const getNotificationPreferences = async (firebase) =>
  apiFetch(firebase, '/api/notifications/preferences', { method: 'GET' });

export const saveNotificationPreference = async (firebase, preference) =>
  apiFetch(firebase, '/api/notifications/preferences', {
    method: 'POST',
    body: JSON.stringify({ preference }),
  });

export const replaceNotificationPreferences = async (firebase, preferences) =>
  apiFetch(firebase, '/api/notifications/preferences', {
    method: 'POST',
    body: JSON.stringify({ preferences }),
  });
